import React, { useState } from "react"
import { Alert, Empty, InputNumber, Select, Table, Tag } from "antd"
import { useQuery } from "@tanstack/react-query"
import SegmentWrapper from "components/custom/SegmentWrapper"
import FeatureHint from "components/new-stuff/ui/FeatureHint"
import {
  ILeaveDepartmentOption,
  leaveService,
  LEAVE_ENDPOINTS,
  IPendingLeaveRow,
} from "services/leaveService"

/**
 * RP-03. Leave still waiting on a decision, oldest first.
 *
 * V2 has no escalation, delegation or reminders — out of scope by decision — so this screen
 * is the mitigation rather than a convenience: it is the only way to see what has stalled
 * and who to chase. That is why it leads with days waiting and the current holder, and why
 * requests the workflow cannot route are counted rather than quietly omitted.
 */
interface PendingLeaveReportProps {
  /**
   * Renders without its own card. The reports screen shows this as one tab among four, and a
   * card inside a card reads as a second, duplicated panel — which is exactly how it looked
   * stacked above the analytics.
   */
  embedded?: boolean
}

const PendingLeaveReport: React.FC<PendingLeaveReportProps> = ({
  embedded = false,
}) => {
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  )
  const [minimumDays, setMinimumDays] = useState<number | undefined>(undefined)

  const { data: departmentsRes } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_DEPARTMENTS],
    queryFn: () => leaveService.getDepartmentOptions(),
  })

  const departments: ILeaveDepartmentOption[] =
    (departmentsRes as { data?: ILeaveDepartmentOption[] } | undefined)?.data ?? []

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      LEAVE_ENDPOINTS.VIEW_PENDING_LEAVE_REPORT,
      departmentId,
      minimumDays,
    ],
    queryFn: () =>
      leaveService.getPendingLeaveReport({
        department_id: departmentId,
        minimum_days_waiting: minimumDays,
      }),
    staleTime: 0,
  })

  const report = data?.data
  const rows = report?.rows ?? []

  const columns = [
    {
      title: "Employee",
      render: (_: unknown, r: IPendingLeaveRow) => (
        <div className="flex flex-col">
          <span>{r.full_name ?? "—"}</span>
          <span className="font-avenir text-xs text-padeSubtext">
            {r.department ?? "—"}
          </span>
        </div>
      ),
    },
    {
      title: "Leave",
      render: (_: unknown, r: IPendingLeaveRow) => (
        <div className="flex flex-col">
          <span>{r.leave_policy ?? "—"}</span>
          <span className="font-avenir text-xs text-padeSubtext">
            {r.first_day} – {r.last_day}
            {r.days != null ? ` · ${r.days} days` : ""}
          </span>
        </div>
      ),
    },
    {
      title: (
        <span className="flex items-center gap-1.5">
          Waiting
          <FeatureHint>
            Days since the request last moved, not since it was submitted. A request that
            passed a step yesterday is not stalled however old it is — this is the number worth
            chasing on.
          </FeatureHint>
        </span>
      ) as unknown as string,
      // The number to act on: age with the current holder, not age overall. A request that
      // moved yesterday is not stalled, however old it is.
      render: (_: unknown, r: IPendingLeaveRow) => (
        <div className="flex flex-col">
          <span className="tabular-nums">
            {r.days_with_current_holder}{" "}
            {r.days_with_current_holder === 1 ? "day" : "days"}
          </span>
          {r.days_since_submitted !== r.days_with_current_holder ? (
            <span className="font-avenir text-xs text-padeSubtext tabular-nums">
              {r.days_since_submitted} since submitted
            </span>
          ) : null}
        </div>
      ),
    },
    {
      title: (
        <span className="flex items-center gap-1.5">
          With
          <FeatureHint>
            Whoever the workflow says holds it now — a named person, or a role. "Nobody
            assigned" means the step names a role that no longer exists, so it will not reach
            anyone's approvals list until the workflow is fixed.
          </FeatureHint>
        </span>
      ) as unknown as string,
      render: (_: unknown, r: IPendingLeaveRow) =>
        r.current_holder_kind === "unassigned" ? (
          <Tag color="red">Nobody assigned</Tag>
        ) : (
          <div className="flex flex-col">
            <span>{r.current_holder}</span>
            <span className="font-avenir text-xs text-padeSubtext">
              {r.current_holder_kind === "role" ? "role" : "person"}
              {r.workflow_steps
                ? ` · step ${(r.review_steps_done ?? 0) + 1} of ${
                    r.workflow_steps
                  }`
                : ""}
            </span>
          </div>
        ),
    },
  ]

  const body = (
    <div
      className={
        embedded
          ? "flex flex-col gap-4"
          : "px-4 md:px-6 lg:px-8 xl:px-[60px] pb-6 flex flex-col gap-4"
      }
    >
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={departmentId}
            onChange={setDepartmentId}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            placeholder="All departments"
            className="min-w-[220px]"
            options={departments.map((d) => ({
              label: d.description,
              value: d.departmentId,
            }))}
          />

          <div className="flex items-center gap-2">
            <span className="font-avenir text-sm text-padeSubtext">
              Waiting at least
            </span>
            <InputNumber
              min={0}
              max={365}
              value={minimumDays}
              onChange={(v) => setMinimumDays(v ?? undefined)}
              placeholder="0"
              className="w-[90px]"
            />
            <span className="font-avenir text-sm text-padeSubtext">days</span>
          </div>

          {report ? (
            <span className="font-avenir text-xs text-padeSubtext ml-auto tabular-nums">
              {report.total_pending} pending · longest wait{" "}
              {report.oldest_days_waiting} days
            </span>
          ) : null}
        </div>

        {isError ? (
          <Alert
            type="warning"
            showIcon
            message="Could not load pending leave"
            description="Try again, or reload the page."
          />
        ) : null}

        {report && report.unassigned_count > 0 ? (
          <Alert
            type="error"
            showIcon
            message={`${report.unassigned_count} ${
              report.unassigned_count === 1 ? "request has" : "requests have"
            } nobody to approve them`}
            description="The approval workflow could not resolve a reviewer, so these do not appear in anyone's approvals list and nobody is notified about them. A Primary or Secondary Admin can approve them from the requests list; fixing the workflow is what makes them route on their own. They are listed below."
          />
        ) : null}

        <Table
          rowKey="employee_request_id"
          columns={columns as never}
          dataSource={rows}
          loading={isLoading}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span className="font-avenir text-padeSubtext">
                    {minimumDays
                      ? `Nothing has been waiting ${minimumDays} days or more.`
                      : "No leave is waiting on a decision."}
                  </span>
                }
              />
            ),
          }}
        />
    </div>
  )

  return embedded ? (
    body
  ) : (
    <SegmentWrapper title="Pending leave">{body}</SegmentWrapper>
  )
}

export default PendingLeaveReport
